'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';

import { Dashboard } from '@/shared/i18n';
import { createEventBus } from '@/shared/utils';
import { DashboardEventKeys, DashboardEvents } from 'src/modules/dashboard/events';
import { DashboardScreen, DashboardLoader } from '../../../modules/dashboard';
import { useServerAuth } from '@/auth';
import { useFirebaseAuth } from '@/shared/hooks';

interface DashboardContainerProps {
  dict: Dashboard;
}

const DashboardContainer = ({ dict }: DashboardContainerProps) => {
  const dashboardEventBus = createEventBus<DashboardEvents>('dashboard-scope');

  const router = useRouter();
  const { user, hydrated } = useServerAuth();
  const auth = useFirebaseAuth();

  const handleRedirect = React.useCallback(
    (params: { url: string; isExternalPage: boolean }) => {
      if (params.isExternalPage) {
        window.open(params.url, '_blank', 'noopener,noreferrer');
        return;
      }
      router.push(params.url);
    },
    [router],
  );

  React.useEffect(() => {
    if (hydrated && !user?.email) {
      handleRedirect({ url: '/auth/login', isExternalPage: false });
    }

    if (user?.email) {
      dashboardEventBus.emit(DashboardEventKeys.customer.view, { email: user.email });
    }

    dashboardEventBus.on(DashboardEventKeys.redirects.to, handleRedirect);
    return () => {
      dashboardEventBus.off(DashboardEventKeys.redirects.to, handleRedirect);
    };
  }, [user?.email, dashboardEventBus, user]);

  if (!user?.email || !hydrated || !auth) return <DashboardLoader key="profile-loader" />;
  return (
    <DashboardScreen auth={auth} eventBus={dashboardEventBus} dict={dict} key="dashboard-screen" />
  );
};

export default DashboardContainer;
